import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { ReplaySubject, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-select';
import { RepairCodeService } from '../../../services/repair-code.service';
import { RepairCodeHeader } from '../../../models/repair-code.model';
import { RepairCodeFilterForm } from '../../../form-model/repair-code-filter.form.model';
import { RepairCodeFilter } from '../../../models/repair-code-filter.model';
import { RepairCodeSearchTextForm } from '../../../form-model/repair-code-search-text.form.model';
import { RepairCodeListGridColumns } from './repair-code-grid.config';
import { AuthPolicies } from '../../../auth/auth-policies';

@Component({
  selector: 'app-repair-code-grid',
  imports: [CommonModule, RouterModule, NgxSkeletonLoaderModule, LoaderComponent],
  templateUrl: './repair-code-grid.component.html',
  styleUrl: './repair-code-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairCodeGridComponent implements OnInit, AfterViewInit, OnDestroy {
  data: RepairCodeHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 30 }, (_, i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: RepairCodeFilter = {};
  getRepairCodeApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairCodeFilterForm: FormGroup<RepairCodeFilterForm>;
    repairCodeSearchTextForm: FormGroup<RepairCodeSearchTextForm>;
  }>;
  columns = RepairCodeListGridColumns;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private readonly repairCodeService: RepairCodeService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit(): void {
    this.urlNavigate = '/master-data/repair-code';
    this.getRepairCodeList();
  }

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.controls.repairCodeFilterForm
      .get('applyRepairCodeFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((applyVal) => {
        if (applyVal === true) {
          this.onApplyFilter();
        }
      });
    this.filterFormGroup.controls.repairCodeSearchTextForm
      .get('appliedRepairCodeSearchTextFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((searchApplied) => {
        if (searchApplied === true) {
          this.onApplyFilter();
        }
      });
  }

  onSortDirectionChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const sortColumn: string = customEvent.detail.column;
    const sortDirection: string = customEvent.detail.direction === 'ascending' ? 'asc' : 'desc';
    if (sortColumn !== 'id') {
      // Map display column ids to API field names
      const columnApiMap: Record<string, string> = {
        isActiveDisplay: 'isActive',
        allowPartsDisplay: 'allowParts',
        multipleEqDisplay: 'multipleEq',
      };
      const apiColumn = columnApiMap[sortColumn] ?? sortColumn;
      this.sort = `${apiColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getRepairCodeList();
    }
  }

  onPageLimitChange(event: Event): void {
    const customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getRepairCodeList();
  }

  onPageChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const changedPage = customEvent.detail as number;
    if (changedPage !== this.currentPage) {
      this.currentPage = changedPage;
      this.getRepairCodeList(true);
    }
  }

  onApplyFilter(): void {
    if (this.filterFormGroup.controls.repairCodeFilterForm.valid) {
      this.filterParam = this.extractFilterParams();
      this.currentPage = 1;
      this.getRepairCodeList();
    }
  }

  private extractFilterParams(): RepairCodeFilter {
    const form = this.filterFormGroup.controls.repairCodeFilterForm;
    const filters: RepairCodeFilter = {};

    const modeList = form.get('repairModeList')?.value;
    if (modeList && Array.isArray(modeList) && modeList.length > 0) {
      filters.repairModeList = modeList;
    }

    const codeList = form.get('repairCodeList')?.value;
    if (codeList && Array.isArray(codeList) && codeList.length > 0) {
      filters.repairCodeList = codeList;
    }

    const description = form.get('description')?.value;
    if (description) {
      filters.description = description;
    }

    const isActive = form.get('isActive')?.value as string[] | null;
    if (isActive && Array.isArray(isActive) && isActive.length > 0) {
      filters.isActive = isActive;
    }

    const allowParts = form.get('allowParts')?.value as string[] | null;
    if (allowParts && Array.isArray(allowParts) && allowParts.length > 0) {
      filters.allowParts = allowParts;
    }

    const multipleEq = form.get('multipleEq')?.value as string[] | null;
    if (multipleEq && Array.isArray(multipleEq) && multipleEq.length > 0) {
      filters.multipleEq = multipleEq;
    }

    const searchText = this.filterFormGroup.controls.repairCodeSearchTextForm.get('SearchText')?.value;
    if (searchText) {
      filters.SearchText = searchText;
    }

    return filters;
  }

  getRepairCodeList(isPageChange: boolean = false): void {
    this.getRepairCodeApiCallStatus = 'IN_PROGRESS';
    this.repairCodeService
      .getRepairCodeList(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body;
          if (headers) {
            this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
            this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
            this.currentPage = +headers.get('Current-Page')! || this.currentPage;
            this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
            this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
            this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          }
          this.getRepairCodeApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: () => {
          this.getRepairCodeApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }

  clearFilter(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairCodeSearchTextForm.get('appliedRepairCodeSearchTextFilter')?.setValue(true);
    this.filterFormGroup.controls.repairCodeFilterForm.get('applyRepairCodeFilter')?.setValue(true);
  }
}
